import React from 'react';
import { BarChart } from '../../components/ui/BarChart';
import { DataTable } from '../../components/ui/DataTable';
import { DashboardIcon } from '../../components/ui/DashboardIcon';
import { DashboardIconType } from '../../components/ui/DashboardIcon';
import { EmptyState } from '../../components/ui/EmptyState';
import { FleetMetricCard } from '../../components/ui/FleetMetricCard';
import { IconButton } from '../../components/ui/IconButton';
import { Panel } from '../../components/ui/Panel';
import { PanelHeader } from '../../components/ui/PanelHeader';
import { StatusPill } from '../../components/ui/StatusPill';
import {
  FleetDashboardAlert,
  FleetDashboardAssignment,
  FleetDashboardMetric,
  FleetDashboardResponse,
  getFleetDashboard,
} from '../../services/fleet';

const demoDashboard: FleetDashboardResponse = {
  stats: {
    activeVehicles: { value: 42, delta: 3 },
    onDutyDrivers: { value: 38, delta: 5 },
    activeRoutes: { value: 6, delta: 0 },
    openAlerts: { value: 2, delta: -1 },
  },
  activity: [
    { day: 'Mon', value: 248 },
    { day: 'Tue', value: 312 },
    { day: 'Wed', value: 220 },
    { day: 'Thu', value: 352 },
    { day: 'Fri', value: 368 },
    { day: 'Sat', value: 280 },
    { day: 'Sun', value: 180 },
  ],
  alerts: [
    { title: 'R-14 delay', message: 'Vehicle near Ozumba Mbadiwe stuck in traffic', type: 'warning' },
    { title: 'Maintenance overdue', message: 'LAG-339-IJ scheduled service missed', type: 'danger' },
    { title: 'Driver check-in', message: 'Ngozi Eze missed morning briefing', type: 'info' },
  ],
  recentAssignments: [
    { route: 'R-12 VI Loop', driver: 'Ade Bakare', vehicle: 'LAG-234-AB', schedule: '08:00 - 18:00', status: 'in_progress' },
    { route: 'R-08 Ikeja Express', driver: 'Mira Okafor', vehicle: 'LAG-119-KJ', schedule: '09:00 - 17:00', status: 'scheduled' },
    { route: 'R-21 Lekki Shuttle', driver: 'Tunde Bello', vehicle: 'LAG-542-TR', schedule: '07:30 - 15:30', status: 'in_progress' },
  ],
};

export function FleetDashboard({ demoMode = false }: { demoMode?: boolean }) {
  const [dashboard, setDashboard] = React.useState<FleetDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let isActive = true;

    if (demoMode) {
      setDashboard(demoDashboard);
      setIsLoading(false);
      return () => {
        isActive = false;
      };
    }

    getFleetDashboard()
      .then((response) => {
        if (isActive) {
          setDashboard(response);
        }
      })
      .catch((requestError) => {
        if (isActive) {
          setError(requestError instanceof Error ? requestError.message : 'Unable to load fleet dashboard');
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [demoMode]);

  if (isLoading) {
    return (
      <section className="fleet-panel empty">
        <EmptyState title="Loading fleet dashboard" message="Fetching the latest fleet activity." />
      </section>
    );
  }

  if (error) {
    return (
      <section className="fleet-panel empty">
        <EmptyState title="Unable to load fleet dashboard" message={error} />
      </section>
    );
  }

  const statCards = getStatCards(dashboard);
  const activity = dashboard?.activity ?? dashboard?.fleet_activity ?? [];
  const alerts = getAlerts(dashboard);
  const assignments = getAssignments(dashboard);
  const activityItems = activity.map((item, index) => {
    const value = item.value ?? item.count ?? 0;
    const label = item.day ?? item.label ?? formatActivityDate(item.date) ?? `Day ${index + 1}`;

    return { label, value };
  });

  return (
    <div className="fleet-dashboard">
      <section className="fleet-stat-grid">
        {statCards.map((stat) => (
          <FleetMetricCard
            delta={stat.delta}
            icon={stat.icon}
            key={stat.label}
            label={stat.label}
            tone={stat.tone}
            value={stat.value}
          />
        ))}
      </section>

      <section className="fleet-main-grid">
        <Panel className="fleet-panel fleet-activity-panel">
          <PanelHeader
            actions={<button className="btn" type="button">Export</button>}
            title="Fleet activity (last 7 days)"
          />
          <BarChart ariaLabel="Fleet activity chart" items={activityItems} />
        </Panel>

        <aside className="fleet-panel fleet-alerts">
          <PanelHeader title="Active alerts" />
          <div className="fleet-alert-list">
            {alerts.length ? alerts.map((alert, index) => (
              <article className="fleet-alert" key={alert.id ?? `${alert.title}-${index}`}>
                <span className={`fleet-icon ${alert.tone}`}>
                  <DashboardIcon type={alert.icon} size={24} />
                </span>
                <div>
                  <strong>{alert.title}</strong>
                  <span>{alert.body}</span>
                </div>
              </article>
            )) : (
              <EmptyState title="No active alerts" message="Your fleet has no alerts requiring attention." />
            )}
          </div>
        </aside>
      </section>

      <Panel className="fleet-panel fleet-assignments">
        <PanelHeader
          actions={<button className="btn" type="button">View all</button>}
          title="Recent assignments"
        />
        <DataTable>
            <thead>
              <tr>
                <th>Route</th>
                <th>Driver</th>
                <th>Vehicle</th>
                <th>Schedule</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {assignments.length ? assignments.map((assignment, index) => (
                <tr key={assignment.id ?? `${assignment.route}-${index}`}>
                  <td><strong>{assignment.route}</strong></td>
                  <td>{assignment.driver}</td>
                  <td>{assignment.vehicle}</td>
                  <td>{assignment.schedule}</td>
                  <td><StatusPill tone={assignment.status === 'scheduled' ? 'info' : 'success'}>{assignment.status}</StatusPill></td>
                  <td>
                    <div className="row-actions">
                      <IconButton ariaLabel={`View ${assignment.route}`} icon="eye" />
                      <IconButton ariaLabel={`Edit ${assignment.route}`} icon="edit" />
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={6}>
                    <EmptyState title="No recent assignments" message="New assignments will appear here." />
                  </td>
                </tr>
              )}
            </tbody>
        </DataTable>
      </Panel>
    </div>
  );
}

function getStatCards(dashboard: FleetDashboardResponse | null) {
  const stats = dashboard?.stats;

  return [
    createStatCard('Active vehicles', stats?.activeVehicles ?? stats?.active_vehicles, dashboard?.active_vehicles_total, 'green', 'truck'),
    createStatCard('On-duty drivers', stats?.onDutyDrivers ?? stats?.on_duty_drivers, dashboard?.on_duty_drivers_total, 'blue', 'user'),
    createStatCard('Active routes', stats?.activeRoutes ?? stats?.active_routes, dashboard?.active_routes_total, 'gold', 'route'),
    createStatCard('Open alerts', stats?.openAlerts ?? stats?.open_alerts, dashboard?.open_alerts_total, 'red', 'alert'),
  ];
}

function createStatCard(label: string, metric: FleetDashboardMetric | undefined, fallbackValue: number | undefined, tone: string, icon: DashboardIconType) {
  const delta = metric?.delta ?? 0;

  return {
    label,
    value: (metric?.value ?? fallbackValue ?? 0).toLocaleString(),
    delta: delta > 0 ? `+${delta}` : String(delta),
    tone,
    icon,
  };
}

function getAlerts(dashboard: FleetDashboardResponse | null) {
  const alerts = dashboard?.alerts ?? dashboard?.active_alerts ?? [];

  return alerts.map((alert: FleetDashboardAlert) => ({
    ...alert,
    title: alert.title ?? 'Fleet alert',
    body: alert.message ?? alert.body ?? 'Review required',
    tone: getAlertTone(alert.type ?? alert.tone),
    icon: (alert.type === 'info' ? 'user' : 'alert') as DashboardIconType,
  }));
}

function getAlertTone(type: string | undefined) {
  if (type === 'danger' || type === 'error' || type === 'critical') return 'red';
  if (type === 'info') return 'blue';
  return 'gold';
}

function getAssignments(dashboard: FleetDashboardResponse | null) {
  const assignments = dashboard?.recentAssignments ?? dashboard?.recent_assignments ?? [];

  return assignments.map((assignment: FleetDashboardAssignment) => ({
    ...assignment,
    route: assignment.route ?? assignment.route_name ?? 'Unassigned route',
    driver: assignment.driver ?? assignment.driver_name ?? 'Unassigned',
    vehicle: assignment.vehicle ?? assignment.vehicle_registration_number ?? assignment.vehicle_plate_number ?? 'Unassigned',
    schedule: assignment.schedule ?? 'Not scheduled',
    status: (assignment.status ?? 'scheduled').replace(/_/g, ' '),
  }));
}

function formatActivityDate(date: string | undefined) {
  if (!date) return undefined;

  const parsedDate = new Date(`${date}T00:00:00`);
  return Number.isNaN(parsedDate.getTime())
    ? date
    : parsedDate.toLocaleDateString(undefined, { weekday: 'short' });
}
